/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Fetches and holds this user's permission snapshot.
 *
 * Resilience deliberately copies useModuleEntitlements: keep the previous value
 * on a transient failure, retry with backoff, and expose `ready` separately from
 * "has data". That hook's comment — "keep prior codes on transient failures to
 * avoid permanent deny-all" — describes exactly the failure this must not have.
 *
 * The direction of failure matters and is chosen on purpose: if permissions
 * cannot be loaded, the UI stays permissive rather than locking the user out.
 * The backend is the real gate; the frontend only hides affordances it knows are
 * pointless. A network blip must never empty someone's CRM.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './Auth';

const PermissionsContext = createContext(null);

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 1500;

const EMPTY = {
  snapshot: null,
  subscription: null,
  ready: false,
  mode: 'legacy',
  isSuper: false,
  allowedRouteKeys: new Set(),
  governedRouteKeys: new Set(),
  capabilities: new Set(),
  moduleCodes: [],
  refresh: () => {},
};

export function PermissionsProvider({ children }) {
  const { axiosInstance, token, isAuthReady, isAuthenticated } = useAuth();

  const [snapshot, setSnapshot] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const snapshotRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    []
  );

  const fetchPermissions = useCallback(async () => {
    // A token handoff updates `token` before /verify completes; issuing a
    // protected request in that window 401s and would burn a retry.
    if (!isAuthReady || !isAuthenticated || !axiosInstance || !token) return;

    try {
      const { data } = await axiosInstance.get('/permissions/me');
      setSnapshot(data);
      setHasLoadedOnce(true);
      retryCountRef.current = 0;
    } catch (error) {
      // Keep whatever we had. Dropping to "no permissions" here would blank the
      // navigation for every user the moment the endpoint hiccups.
      setSnapshot(snapshotRef.current);

      if (!hasLoadedOnce && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(
          fetchPermissions,
          RETRY_DELAY_MS * retryCountRef.current
        );
      }
    }
  }, [axiosInstance, token, isAuthReady, isAuthenticated, hasLoadedOnce]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    // Roles, teams and assignments are edited in Settings; the same window-event
    // pattern the sidenav preferences already use avoids a full reload.
    const handler = () => fetchPermissions();
    window.addEventListener('permissionsUpdated', handler);
    // A new token can mean a different tenant entirely.
    window.addEventListener('auth-token-updated', handler);
    return () => {
      window.removeEventListener('permissionsUpdated', handler);
      window.removeEventListener('auth-token-updated', handler);
    };
  }, [fetchPermissions]);

  const value = useMemo(
    () => ({
      snapshot,
      ready: hasLoadedOnce,
      mode: snapshot?.mode ?? 'legacy',
      isSuper: Boolean(snapshot?.is_super),
      allowedRouteKeys: new Set(snapshot?.route_keys?.allowed ?? []),
      // The keys the server has an opinion about. Anything outside it is left
      // to the entitlement layers, so enforcement cannot blank a page the
      // permission model does not actually govern.
      governedRouteKeys: new Set(snapshot?.route_keys?.governed ?? []),
      capabilities: new Set(snapshot?.capabilities ?? []),
      moduleCodes: snapshot?.module_codes ?? [],
      // Expiry / grace state, carried on the same snapshot so the banner does
      // not need a request of its own -- and cannot disagree with the sidebar.
      subscription: snapshot?.subscription ?? null,
      refresh: fetchPermissions,
    }),
    [snapshot, hasLoadedOnce, fetchPermissions]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/** Safe outside the provider: returns inert defaults rather than throwing. */
export function usePermissionsContext() {
  return useContext(PermissionsContext) ?? EMPTY;
}

/** Broadcast after any role/team/assignment change. */
export function notifyPermissionsChanged() {
  window.dispatchEvent(new Event('permissionsUpdated'));
}

export default PermissionsProvider;
